import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { keys } from "../api/queryKeys.ts";
import { api } from "../api/client.ts";
import DistributionChart from "../components/DistributionChart.tsx";
import CountryName from "../components/CountryName.tsx";
import FormattedDate from "../components/FormattedDate.tsx";
import FilterBar from "../components/filters/FilterBar.tsx";
import SavedViewsMenu from "../components/filters/SavedViewsMenu.tsx";
import type { FilterSpec } from "../components/filters/specs.ts";
import { useFilters } from "../components/filters/useFilters.ts";
import StatusChip from "../components/StatusChip.tsx";
import { MatchedCell, TitleLinkCell } from "../components/table/cells.tsx";
import EmptyTableRow from "../components/table/EmptyTableRow.tsx";
import ScrollTable from "../components/table/ScrollTable.tsx";
import SortableTableHead from "../components/table/SortableTableHead.tsx";
import TableCard from "../components/table/TableCard.tsx";
import UrlTablePagination from "../components/table/UrlTablePagination.tsx";
import { useOrganization } from "../contexts/OrganizationContext.tsx";
import { useTenant } from "../contexts/TenantContext.tsx";
import { useCountryOptions } from "../hooks/useCountryOptions.ts";
import { useSourceStats } from "../hooks/useSourceStats.ts";
import { useCommaParam, useUrlPaging, useUrlSort, useUrlState } from "../hooks/useUrlState.ts";
import { booleanFilterToParam, parseBooleanFilter, parseFiniteNumber } from "../utils/urlFilters.ts";
import { formatNumber, humanize } from "../utils/format.ts";
import PageHeader from "../components/PageHeader.tsx";

const COLUMNS = [
  { id: "title", label: "Title" },
  { id: null, label: "Applicant" },
  { id: null, label: "Country" },
  { id: null, label: "Source" },
  { id: null, label: "Status" },
  { id: "filed_at", label: "Filed" },
  { id: "hearing_deadline", label: "Hearing" },
  { id: "capacity_mw", label: "MW" },
  { id: null, label: "Matched" },
];

function RegulatoryFilingsPage() {
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const { currentOrg } = useOrganization();
  const url = useUrlState();
  const { searchParams } = url;

  const { data: filterOptions } = useQuery({
    queryKey: keys.regulatoryFiling.filterOptions(),
    queryFn: () => api.getRegulatoryFilingFilterOptions(),
    staleTime: 30 * 60 * 1000,
  });
  const countryOptions = useCountryOptions(filterOptions?.countries);

  const specs = useMemo<FilterSpec[]>(() => {
    const loading = !filterOptions;
    return [
      {
        kind: "options",
        key: "country",
        label: "Country",
        param: "country",
        pinned: true,
        loading,
        options: countryOptions,
        searchPlaceholder: "Search countries",
      },
      {
        kind: "options",
        key: "status",
        label: "Status",
        param: "status",
        pinned: true,
        loading,
        options: (filterOptions?.statuses ?? []).map((s) => ({ value: s, label: humanize(s) })),
      },
      { kind: "text", key: "applicant", label: "Applicant", param: "applicant", placeholder: "Applicant name" },
      {
        kind: "options",
        key: "source",
        label: "Source",
        param: "source",
        loading,
        options: (filterOptions?.sources ?? []).map((s) => ({ value: s, label: s })),
      },
      { kind: "boolean", key: "active", label: "Active", param: "active" },
      {
        kind: "boolean",
        key: "matched",
        label: "Matched",
        param: "matched",
        labels: { yes: "Matched", no: "Unmatched" },
      },
      { kind: "dateRange", key: "filed", label: "Filed", fromParam: "filed_from", toParam: "filed_to" },
      {
        kind: "dateRange",
        key: "hearing",
        label: "Hearing deadline",
        fromParam: "hearing_from",
        toParam: "hearing_to",
      },
      { kind: "range", key: "min_mw", label: "Min capacity", minParam: "min_mw", unit: "MW", decimal: true },
    ];
  }, [filterOptions, countryOptions]);

  const filters = useFilters(specs, {
    state: url,
    search: { param: "title", placeholder: "Search filings" },
    pageKey: "regulatory-filings",
    scopeId: null,
    remember: true,
  });

  const countries = useCommaParam(searchParams, "country");
  const sources = useCommaParam(searchParams, "source");
  const statuses = useCommaParam(searchParams, "status");
  const param = (key: string) => searchParams.get(key) || undefined;
  const yesNo = (key: string) => booleanFilterToParam(parseBooleanFilter(searchParams.get(key)));

  const { sortBy, sortDir, handleSort } = useUrlSort(url, "filed_at", "desc");
  const paging = useUrlPaging(url);

  const queryParams = {
    title: param("title"),
    applicant_name: param("applicant"),
    country: countries.length ? countries : undefined,
    source: sources.length ? sources : undefined,
    status: statuses.length ? statuses : undefined,
    is_active: yesNo("active"),
    matched: yesNo("matched"),
    filed_from: param("filed_from"),
    filed_to: param("filed_to"),
    hearing_from: param("hearing_from"),
    hearing_to: param("hearing_to"),
    min_capacity_mw: parseFiniteNumber(searchParams.get("min_mw") ?? ""),
    sort_by: sortBy,
    sort_dir: sortDir,
    limit: paging.rowsPerPage,
    offset: paging.offset,
  };

  const { data, isLoading } = useQuery({
    queryKey: keys.regulatoryFiling.list(queryParams),
    queryFn: () => api.getRegulatoryFilings(queryParams),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const stats = useSourceStats(
    keys.regulatoryFiling.stats(),
    api.getRegulatoryFilingStats,
    (s) => ({ label: s.source, count: s.count }),
  );

  return (
    <>
      <PageHeader
        title="Regulatory filings"
        description="Permit applications and decisions from public regulators, matched to the applicants"
      />
      <DistributionChart
        title="Filings per source"
        data={stats.chartData}
        loading={stats.isLoading}
      />
      <TableCard
        loading={isLoading}
        toolbar={
          <FilterBar
            filters={filters}
            count={data ? `${formatNumber(data.total)} ${data.total === 1 ? "filing" : "filings"}` : undefined}
            views={
              <SavedViewsMenu
                filters={filters}
                context={{ organizationId: currentOrg?.id, organizationName: currentOrg?.name }}
              />
            }
          />
        }
        footer={data && data.total > 0 && <UrlTablePagination total={data.total} paging={paging} />}
      >
        <ScrollTable>
          <SortableTableHead
            columns={COLUMNS}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
          />
          <TableBody>
            {data?.items.map((f) => (
              <TableRow
                key={f.id}
                hover
                sx={{ cursor: f.master_company_id ? "pointer" : "default" }}
                onClick={() => {
                  if (f.master_company_id) {
                    navigate(tenantPath(`/companies/${f.master_company_id}`));
                  }
                }}
              >
                <TitleLinkCell title={f.title} href={f.source_url} />
                <TableCell>{f.master_company_name ?? f.applicant_name}</TableCell>
                <TableCell>
                  <CountryName code={f.country} />
                </TableCell>
                <TableCell>{f.source}</TableCell>
                <TableCell>
                  <StatusChip
                    kind="regulatory_filing"
                    value={f.status}
                    label={f.status_raw ?? undefined}
                    variant={f.is_active ? "filled" : "outlined"}
                  />
                </TableCell>
                <TableCell>
                  <FormattedDate value={f.filed_at} />
                </TableCell>
                <TableCell>
                  <FormattedDate value={f.hearing_deadline} />
                </TableCell>
                <TableCell>{f.capacity_mw ?? "-"}</TableCell>
                <MatchedCell matched={!!f.master_company_id} />
              </TableRow>
            ))}
            {data?.items.length === 0 && (
              <EmptyTableRow colSpan={COLUMNS.length}>
                {filters.activeCount > 0 ? "No filings match your filters" : "No filings found"}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>
    </>
  );
}

export default RegulatoryFilingsPage;
